import React, { useState } from 'react';
import { CheckCircle, Calendar, User, MapPin, Phone, Mail, FileText, Download, ExternalLink, Clock, ChevronDown, ChevronUp, Bell, Building2, Shield } from 'lucide-react';
import Button from '../../../components/ui/Button';

// ─── Preparation Checklist ────────────────────────────────────────────────────

const LEGAL_CHECKLIST = [
  { id: 'id_doc', label: "Pièce d'identité valide (CNI ou passeport)", category: 'Identité' },
  { id: 'title_deed', label: 'Titre foncier ou acte de propriété', category: 'Documents fonciers' },
  { id: 'tax_cert', label: 'Certificat de situation fiscale', category: 'Fiscal' },
  { id: 'urban_cert', label: "Certificat d'urbanisme", category: 'Urbanisme' },
  { id: 'sale_promise', label: 'Promesse de vente signée (si applicable)', category: 'Contrats' },
  { id: 'bank_details', label: 'Relevé d\'identité bancaire (RIB)', category: 'Financier' },
  { id: 'marriage_cert', label: 'Acte de mariage ou jugement de divorce (si applicable)', category: 'État civil' },
  { id: 'power_atty', label: 'Procuration notariée (si représenté)', category: 'Représentation' },
];

const PROPERTY_CHECKLIST = [
  { id: 'prop_photos', label: 'Photos récentes du bien', category: 'Documentation' },
  { id: 'prop_plans', label: 'Plans du bien (si disponibles)', category: 'Documentation' },
  { id: 'budget_range', label: 'Budget et fourchette de prix définis', category: 'Financier' },
  { id: 'commune_pref', label: 'Communes préférées listées', category: 'Localisation' },
  { id: 'questions', label: 'Liste de questions préparées', category: 'Préparation' },
  { id: 'financing', label: 'Preuve de financement ou pré-accord bancaire', category: 'Financier' },
];

const ChecklistItem = ({ item, checked, onToggle }) => (
  <button
    onClick={() => onToggle(item?.id)}
    className="w-full flex items-start gap-3 p-2.5 rounded-xl hover:bg-surface/60 transition-colors text-left group"
  >
    <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
      checked ? 'bg-brand-earth border-brand-earth' : 'border-border/60 group-hover:border-brand-earth/50'
    }`}>
      {checked && <CheckCircle className="w-3 h-3 text-white" />}
    </div>
    <div className="flex-1 min-w-0">
      <p className={`text-sm transition-colors ${checked ? 'line-through text-text-secondary' : 'text-text-primary'}`}>
        {item?.label}
      </p>
      <span className="text-[10px] text-text-secondary/70 font-medium">{item?.category}</span>
    </div>
  </button>
);

// ─── Calendar Sync Options ────────────────────────────────────────────────────

const CalendarSyncSection = ({ bookingData, appointment }) => {
  const [synced, setSynced] = useState(null);

  const buildGoogleCalendarUrl = () => {
    const start = new Date(bookingData?.dateTime);
    const end = new Date(start.getTime() + (bookingData?.service?.duration || 60) * 60000);
    const fmt = (d) => d?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
    const consultantName = bookingData?.consultant?.notary_name ||
      bookingData?.consultant?.user_profile?.full_name ||
      bookingData?.consultant?.name || 'Consultant';
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: `${bookingData?.service?.name} — KapitalEstates`,
      dates: `${fmt(start)}/${fmt(end)}`,
      details: `Consultation avec ${consultantName}\nRéférence: ${appointment?.id?.slice(-8)?.toUpperCase() || 'N/A'}\nPlateforme: KapitalEstates`,
      location: bookingData?.service?.id === 'legal_consultation' ? (bookingData?.consultant?.office_address ||'Bureau du notaire')
        : 'Consultation en ligne',
    });
    return `https://calendar.google.com/calendar/render?${params?.toString()}`;
  };

  const buildICSContent = () => {
    const start = new Date(bookingData?.dateTime);
    const end = new Date(start.getTime() + (bookingData?.service?.duration || 60) * 60000);
    const fmt = (d) => d?.toISOString()?.replace(/[-:]/g, '')?.split('.')?.[0] + 'Z';
    const consultantName = bookingData?.consultant?.notary_name ||
      bookingData?.consultant?.user_profile?.full_name ||
      bookingData?.consultant?.name || 'Consultant';
    return [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//KapitalEstates//FR',
      'BEGIN:VEVENT',
      `UID:${appointment?.id || Date.now()}@kapitalestates.com`,
      `DTSTART:${fmt(start)}`,
      `DTEND:${fmt(end)}`,
      `SUMMARY:${bookingData?.service?.name} — KapitalEstates`,
      `DESCRIPTION:Consultation avec ${consultantName}\\nRéférence: ${appointment?.id?.slice(-8)?.toUpperCase() || 'N/A'}`,
      `LOCATION:${bookingData?.service?.id === 'legal_consultation' ? (bookingData?.consultant?.office_address || 'Bureau du notaire') : 'Consultation en ligne'}`,
      'END:VEVENT',
      'END:VCALENDAR',
    ]?.join('\r\n');
  };

  const downloadICS = () => {
    const blob = new Blob([buildICSContent()], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rdv-kapitalestates-${appointment?.id?.slice(-8) || 'consultation'}.ics`;
    a?.click();
    URL.revokeObjectURL(url);
    setSynced('ics');
  };

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <a
          href={buildGoogleCalendarUrl()}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => setSynced('google')}
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border/50 hover:border-brand-earth/40 hover:bg-brand-earth/5 transition-all group"
        >
          <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center flex-shrink-0">
            <Calendar className="w-3.5 h-3.5 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-text-primary">Google Calendar</p>
            <p className="text-[10px] text-text-secondary">Ajouter l'événement</p>
          </div>
          {synced === 'google' ? (
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
          ) : (
            <ExternalLink className="w-3 h-3 text-text-secondary group-hover:text-brand-earth flex-shrink-0" />
          )}
        </a>

        <button
          onClick={downloadICS}
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border/50 hover:border-brand-earth/40 hover:bg-brand-earth/5 transition-all group"
        >
          <div className="w-7 h-7 rounded-lg bg-blue-50 flex items-center justify-center flex-shrink-0">
            <Download className="w-3.5 h-3.5 text-blue-500" />
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="text-xs font-semibold text-text-primary">Apple / Outlook</p>
            <p className="text-[10px] text-text-secondary">Télécharger .ics</p>
          </div>
          {synced === 'ics' ? (
            <CheckCircle className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
          ) : (
            <Download className="w-3 h-3 text-text-secondary group-hover:text-brand-earth flex-shrink-0" />
          )}
        </button>

        <a
          href={`/mon-espace`}
          className="flex items-center gap-2 px-3 py-2.5 rounded-xl border border-border/50 hover:border-brand-earth/40 hover:bg-brand-earth/5 transition-all group"
        >
          <div className="w-7 h-7 rounded-lg bg-brand-earth/10 flex items-center justify-center flex-shrink-0">
            <Bell className="w-3.5 h-3.5 text-brand-earth" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-text-primary">Mon Espace</p>
            <p className="text-[10px] text-text-secondary">Voir mes rendez-vous</p>
          </div>
          <ExternalLink className="w-3 h-3 text-text-secondary group-hover:text-brand-earth flex-shrink-0" />
        </a>
      </div>
    </div>
  );
};

// ─── Notary Details Card ──────────────────────────────────────────────────────

const NotaryDetailsCard = ({ consultant, service }) => {
  if (!consultant || service?.id !== 'legal_consultation') return null;

  const name = consultant?.notary_name || consultant?.user_profile?.full_name || consultant?.name || 'Notaire';
  const email = consultant?.email || consultant?.user_profile?.email || '';
  const phone = consultant?.phone || consultant?.user_profile?.phone || '';
  const address = consultant?.office_address || '';
  const commune = consultant?.commune || '';
  const isVerified = consultant?.is_verified;

  return (
    <div className="bg-gradient-to-br from-brand-earth/5 to-brand-gold/5 border border-brand-earth/20 rounded-2xl p-4">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-xl bg-brand-earth/10 flex items-center justify-center flex-shrink-0">
          <Building2 className="w-6 h-6 text-brand-earth" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-bold text-text-primary">{name}</p>
            {isVerified && (
              <span className="flex items-center gap-1 px-1.5 py-0.5 bg-emerald-50 border border-emerald-200 rounded-full">
                <Shield className="w-2.5 h-2.5 text-emerald-600" />
                <span className="text-[9px] font-bold text-emerald-600">Vérifié KPS</span>
              </span>
            )}
          </div>
          <p className="text-xs text-text-secondary mt-0.5">Notaire agréé — {commune || 'Abidjan'}</p>
        </div>
      </div>

      <div className="mt-3 space-y-2">
        {address && (
          <div className="flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-text-secondary mt-0.5 flex-shrink-0" />
            <p className="text-xs text-text-primary">{address}</p>
          </div>
        )}
        {phone && (
          <div className="flex items-center gap-2">
            <Phone className="w-3.5 h-3.5 text-text-secondary flex-shrink-0" />
            <a href={`tel:${phone}`} className="text-xs text-brand-earth hover:underline font-medium">{phone}</a>
          </div>
        )}
        {email && (
          <div className="flex items-center gap-2">
            <Mail className="w-3.5 h-3.5 text-text-secondary flex-shrink-0" />
            <a href={`mailto:${email}`} className="text-xs text-brand-earth hover:underline font-medium">{email}</a>
          </div>
        )}
      </div>

      <div className="mt-3 pt-3 border-t border-brand-earth/15">
        <p className="text-[10px] text-text-secondary font-medium uppercase tracking-wide mb-1.5">Informations importantes</p>
        <ul className="space-y-1">
          {[
            'Arrivez 10 minutes avant l\'heure prévue',
            'Apportez tous les documents originaux',
            'Les photocopies seront réalisées sur place',
          ]?.map((tip, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <span className="w-1 h-1 rounded-full bg-brand-earth/60 mt-1.5 flex-shrink-0" />
              <span className="text-[11px] text-text-secondary">{tip}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── Main PostBookingWorkflow ─────────────────────────────────────────────────

const PostBookingWorkflow = ({ appointment, bookingData, onClose, onNewBooking }) => {
  const [checkedItems, setCheckedItems] = useState({});
  const [checklistOpen, setChecklistOpen] = useState(true);
  const [calendarOpen, setCalendarOpen] = useState(true);

  const checklist = bookingData?.service?.id === 'legal_consultation' ? LEGAL_CHECKLIST : PROPERTY_CHECKLIST;
  const checkedCount = Object.values(checkedItems)?.filter(Boolean)?.length;
  const progress = Math.round((checkedCount / checklist?.length) * 100);

  const toggleItem = (id) => setCheckedItems(prev => ({ ...prev, [id]: !prev?.[id] }));

  const formatDateTime = (dt) => {
    if (!dt) return '—';
    return new Date(dt)?.toLocaleDateString('fr-FR', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  const consultantName = bookingData?.consultant?.notary_name ||
    bookingData?.consultant?.user_profile?.full_name ||
    bookingData?.consultant?.name || 'Consultant';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-xl">

        {/* ── Header ── */}
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-base font-bold text-foreground">Réservation confirmée !</h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {bookingData?.service?.name} · {formatDateTime(bookingData?.dateTime)}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-accent transition-colors text-muted-foreground"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-5">

          {/* ── Confirmation Banner ── */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-emerald-800">
                Votre rendez-vous est confirmé avec {consultantName}
              </p>
              <p className="text-xs text-emerald-700 mt-0.5">
                Un email de confirmation a été envoyé. Vous recevrez un rappel 24h avant.
              </p>
              {appointment?.id && (
                <p className="text-[10px] font-mono text-emerald-600 mt-1">
                  Réf : {appointment?.id?.slice(-8)?.toUpperCase()}
                </p>
              )}
            </div>
          </div>

          {/* ── Notary Details (legal only) ── */}
          <NotaryDetailsCard consultant={bookingData?.consultant} service={bookingData?.service} />

          {/* ── Appointment Summary ── */}
          <div className="bg-muted/40 border border-border rounded-xl p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Récapitulatif</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <Calendar className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground">Date & Heure</p>
                  <p className="text-xs font-semibold text-foreground capitalize">{formatDateTime(bookingData?.dateTime)}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Clock className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground">Durée</p>
                  <p className="text-xs font-semibold text-foreground">{bookingData?.service?.duration} min</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <User className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground">Consultant</p>
                  <p className="text-xs font-semibold text-foreground">{consultantName}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-[10px] text-muted-foreground">Lieu</p>
                  <p className="text-xs font-semibold text-foreground">
                    {bookingData?.service?.id === 'legal_consultation' ? (bookingData?.consultant?.office_address ||'Bureau du notaire')
                      : 'Consultation en ligne'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* ── Calendar Sync ── */}
          <div className="border border-border rounded-xl overflow-hidden">
            <button
              onClick={() => setCalendarOpen(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-brand-earth" />
                <span className="text-sm font-semibold text-foreground">Synchroniser le calendrier</span>
              </div>
              {calendarOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>
            {calendarOpen && (
              <div className="px-4 pb-4 border-t border-border/50">
                <p className="text-xs text-muted-foreground mt-3 mb-3">
                  Ajoutez ce rendez-vous à votre calendrier pour ne pas l'oublier.
                </p>
                <CalendarSyncSection bookingData={bookingData} appointment={appointment} />
              </div>
            )}
          </div>

          {/* ── Preparation Checklist ── */}
          <div className="border border-border rounded-xl overflow-hidden">
            <button
              onClick={() => setChecklistOpen(p => !p)}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
            >
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-brand-earth" />
                <span className="text-sm font-semibold text-foreground">Checklist de préparation</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                  progress === 100 ? 'bg-emerald-100 text-emerald-700' : 'bg-brand-gold/15 text-brand-gold'
                }`}>
                  {checkedCount}/{checklist?.length}
                </span>
              </div>
              {checklistOpen ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
            </button>

            {checklistOpen && (
              <div className="px-4 pb-4 border-t border-border/50">
                {/* Progress bar */}
                <div className="mt-3 mb-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] text-muted-foreground">Progression</p>
                    <p className="text-[10px] font-bold text-brand-earth">{progress}%</p>
                  </div>
                  <div className="h-1.5 bg-border/40 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-brand-earth rounded-full transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>

                <div className="space-y-0.5">
                  {checklist?.map(item => (
                    <ChecklistItem
                      key={item?.id}
                      item={item}
                      checked={!!checkedItems?.[item?.id]}
                      onToggle={toggleItem}
                    />
                  ))}
                </div>

                {progress === 100 && (
                  <div className="mt-3 flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    <p className="text-xs font-semibold text-emerald-700">Vous êtes prêt(e) pour votre rendez-vous !</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Next Steps ── */}
          <div className="border-t border-border pt-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-3">Prochaines étapes</p>
            <ol className="space-y-2.5">
              {[
                { title: 'Email de confirmation', desc: 'Vérifiez votre boîte mail pour les détails complets.' },
                { title: 'Préparez vos documents', desc: 'Cochez tous les éléments de la checklist ci-dessus.' },
                { title: 'Rappel automatique', desc: 'Vous recevrez un rappel 24h avant votre rendez-vous.' },
                { title: 'Rendez-vous', desc: 'Présentez-vous 10 minutes avant l\'heure prévue.' },
              ]?.map((step, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="w-5 h-5 rounded-full bg-brand-earth/10 text-brand-earth flex items-center justify-center text-[10px] font-bold flex-shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{step?.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{step?.desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="sticky bottom-0 bg-card border-t border-border px-6 py-4 flex flex-col sm:flex-row gap-3">
          <Button variant="outline" size="default" onClick={onNewBooking} className="flex-1">
            Nouvelle réservation
          </Button>
          <Button variant="default" size="default" onClick={onClose} className="flex-1">
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostBookingWorkflow;
